import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, MenuItem, TextField, Typography } from '@mui/material';
import ImageGallery from 'react-image-gallery';
import { DateTime } from 'luxon';
import { getTripVideoUrl } from '../api';
import { setError } from '../alerts';

// Constants
import { DATE_TIME_ZONE_FORMAT } from '../constants';

const VideoRender = ({ video, timezone, defaultTimestamp }) => {
  const [url, setUrl] = React.useState(undefined);

  React.useEffect(() => {
    getTripVideoUrl(video.key).then(url => {
      setUrl(url);
    }).catch(error => {
      setError(error ?? 'Error loading video.');
    });
  }, [video.key]);

  return url ? (
    <div>
      <video width='90%' height='500' controls muted>
        <source
          src={url + `#t=${defaultTimestamp ?? 0.001}`}
          type='video/mp4'
        />
      </video>

      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginTop: 20 }}>
        <Typography variant='body1'><b>Video Start Time:</b> {video.timestamp?.setZone(timezone)?.toLocaleString({ ...DateTime.DATETIME_SHORT, timeZoneName: 'short' })}</Typography>
        <Typography variant='body1'><b>Camera:</b> {video.cameraName}</Typography>
        <Typography variant='body1'><b>Clip Number:</b> {video.clipNumber} of {video.totalNumClips}</Typography>
        {video.fromGlacier &&
        <Typography variant='body1'><b>Video Pulled from Storage</b></Typography>}
      </div>
    </div>
  ) : null;
};
VideoRender.propTypes = {
  video: PropTypes.object.isRequired,
  timezone: PropTypes.string,
  defaultTimestamp: PropTypes.number
};

class VideoGallery extends Component {
  constructor(props) {
    super(props);

    this.state = {
      displayedVideoIndexSelection: '',
      cameraVideoFolders: [],
      selectedCameraIndex: null,
      selectedSessionIndex: null,
    };
  }

  componentDidMount() {
    this.formatProjects();

    if (this.props.videoObject != null) {
      this.checkForVideo();
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.trip !== this.props.trip) {
      this.formatProjects();
    }
    if (prevProps.videoObject !== this.props.videoObject && this.props.videoObject != null) {
      this.checkForVideo();
    }
  }

  checkForVideo = () => {
    if (this.props.videoObject == null || this.props.videoObject.cameraID == null || this.props.videoObject.fishCatchItem == null) return;

    try {
      const cameraID = this.props.videoObject.cameraID;
      const fishCatchItem = this.props.videoObject.fishCatchItem;
      const timestamp = fishCatchItem.timestamp;

      // Select the camera
      const selectedCameraIndex = this.state.cameraVideoFolders.findIndex(cameraFolder => cameraFolder.camera.getID() === cameraID);
      if (selectedCameraIndex === -1) return;
      this.setState({ selectedCameraIndex });

      // Select the session based on the timestamp
      const selectedSessionIndex = this.state.cameraVideoFolders[selectedCameraIndex].sessions.findIndex(session => session.startTimestamp <= timestamp && timestamp <= session.stopTimestamp);
      if (selectedSessionIndex === -1) return;
      this.setState({ selectedSessionIndex });

      // Select the video based on the timestamp
      let sessionVideos = this.state.cameraVideoFolders[selectedCameraIndex].sessions[selectedSessionIndex]?.videos ?? [];
      const selectedVideoIndex = sessionVideos.length - sessionVideos.map(sv => sv.timestamp).reverse().findIndex(t => t <= timestamp) - 1;
      if (selectedVideoIndex === -1) return;

      // Find the difference in the timestamps
      const videoTimestamp = sessionVideos[selectedVideoIndex].timestamp;
      const differenceInSeconds = Math.abs(timestamp - videoTimestamp) / 1000;

      // Update cameraVideoFolders
      const updatedCameraVideoFolders = this.state.cameraVideoFolders;
      updatedCameraVideoFolders[selectedCameraIndex].sessions[selectedSessionIndex].videos[selectedVideoIndex].defaultTimestamp = differenceInSeconds;
      this.setState({ cameraVideoFolders: updatedCameraVideoFolders, displayedVideoIndexSelection: selectedVideoIndex }, () => this.goToClipNumber());
    }
    catch (error) {
      setError('Error: Unable to find video for the selected fish catch item. Video may be archived or unavailable.');
    }
  }

  formatProjects() {
    const haulSessions = [];
    const setSessions = [];

    // Get all haul and set sessions
    for (const set of this.props.trip?.getSets()) {
      setSessions.push({
        startTimestamp: set.getStartTimestamp(),
        stopTimestamp: set.getStopTimestamp()
      });
      if (set.getHaul() != null) {
        haulSessions.push({
          startTimestamp: set.getHaul()?.getStartTimestamp(),
          stopTimestamp: set.getHaul()?.getStopTimestamp()
        });
      }
    }

    let cameraVideoFolders = [];
    const tripCameras = this.props.trip?.getTripCameras()?.filter(tripCamera => tripCamera.getType() != null) ?? [];
    for (const tripCamera of tripCameras) {
      cameraVideoFolders.push({
        camera: tripCamera.getCamera(),
        sessions: tripCamera.getType() === 'HAUL' ? haulSessions.map(session => ({ ...session, videos: [] })) : setSessions.map(session => ({ ...session, videos: [] })),
        glacier: false
      });
    }

    // Add all videos into correct camera/session
    for (const project of this.props.trip?.getProjects()) {
      let cameraVideoFolderIndex = cameraVideoFolders.findIndex(cameraVideoFolder => cameraVideoFolder.camera.getID() === project.getCamera().getID());
      if (cameraVideoFolderIndex === -1) continue;
      if (project.getGlacier()) cameraVideoFolders[cameraVideoFolderIndex].glacier = true;
      for (const videoFolder of project.getVideoFolders()) {
        for (const video of videoFolder.getVideos()) {
          cameraVideoFolders[cameraVideoFolderIndex].sessions.forEach(session => {
            if (session.startTimestamp <= video.getDate() && video.getDate() <= session.stopTimestamp) {
              session.videos.push({
                embedUrl: video.getUrl(),
                timestamp: video.getDate(),
                cameraName: project.getCamera()?.getName(),
                fromGlacier: video.getFromGlacier(),
                key: video.getKey(),
              });
            }
          });
        }
      }
    }

    // Sort videos and add clipNumber and totalNumClips to each video
    for (const cameraVideoFolder of cameraVideoFolders) {
      for (const session of cameraVideoFolder.sessions) {
        session.videos.sort((a, b) => a.timestamp - b.timestamp);
        for (let i = 0; i < session.videos.length; i++) {
          session.videos[i].clipNumber = i + 1;
          session.videos[i].totalNumClips = session.videos.length;
        }
      }
    }

    this.setState({ cameraVideoFolders }, () => this.checkForVideo());
  }

  renderVideo(video) {
    return (
      <VideoRender video={video} timezone={this.props.trip?.getTimezone()} defaultTimestamp={video.defaultTimestamp} />
    );
  }

  goToClipNumber = () => {
    this._imageGallery.slideToIndex(this.state.displayedVideoIndexSelection);
  }

  onSelectCamera = (selectedCameraIndex) => {
    this.setState({ selectedCameraIndex, selectedSessionIndex: null, displayedVideoIndexSelection: '' });
  }

  render() {
    return (
      <div>
        {/* Select Camera */}
        <TextField
          name='selectedCameraIndex'
          label='Camera'
          style={{ width: '100%', marginBottom: '10px' }}
          value={this.state.selectedCameraIndex ?? ''}
          onChange={(e) => this.onSelectCamera(e.target.value)}
          variant='filled'
          select
        >
          {this.state.cameraVideoFolders?.map((cameraFolder, index) => (
            <MenuItem key={index} value={index}>{cameraFolder.camera.getName()}</MenuItem>
          ))}
        </TextField>

        {/* Select Haul/Set Session */}
        <TextField
          name='selectedSessionIndex'
          label='Recording Session'
          style={{ width: '100%', marginBottom: '10px' }}
          value={this.state.selectedSessionIndex ?? ''}
          onChange={(e) => this.setState({ selectedSessionIndex: e.target.value, displayedVideoIndexSelection: '' })}
          variant='filled'
          disabled={this.state.selectedCameraIndex == null}
          select
        >
          {this.state.selectedCameraIndex != null && this.state.selectedCameraIndex < this.state.cameraVideoFolders?.length && this.state.cameraVideoFolders[this.state.selectedCameraIndex]?.sessions?.map((session, index) => (
            <MenuItem key={index} value={index}>{session.startTimestamp.setZone(this.props.trip?.getTimezone())?.toLocaleString(DATE_TIME_ZONE_FORMAT)}</MenuItem>
          ))}
        </TextField>

        {this.state.cameraVideoFolders[this.state.selectedCameraIndex]?.glacier &&
        <Typography variant='body1' style={{ fontWeight: 'bold', margin: '1em', textAlign: 'center' }}>Additional videos may be in storage. Contact FlyWire for more information.</Typography>}

        {this.state.selectedCameraIndex != null && this.state.selectedSessionIndex != null ? (
          this.state.cameraVideoFolders[this.state.selectedCameraIndex]?.sessions[this.state.selectedSessionIndex]?.videos?.length === 0 ? (
            <Typography variant='body1'>No videos captured for selected camera and session.</Typography>
          ) : (
            <div>
              <ImageGallery
                ref={(i) => (this._imageGallery = i)}
                key={this.state.cameraVideoFolders[this.state.selectedCameraIndex]?.sessions[this.state.selectedSessionIndex]?.videos?.length > 0 ? this.state.cameraVideoFolders[this.state.selectedCameraIndex]?.sessions[this.state.selectedSessionIndex]?.videos[0]?.key : 1}
                items={this.state.cameraVideoFolders[this.state.selectedCameraIndex]?.sessions[this.state.selectedSessionIndex]?.videos}
                infinite={false}
                showPlayButton={false}
                showThumbnails={false}
                showFullscreenButton={false}
                lazyLoad
                renderItem={this.renderVideo.bind(this)}
              />

              <div style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}>
                <TextField
                  label='Jump to Clip'
                  value={this.state.displayedVideoIndexSelection}
                  onChange={(e) => this.setState({ displayedVideoIndexSelection: e.target.value })}
                  style={{ width: '150px' }}
                  select
                >
                  {Array.from({ length: this.state.cameraVideoFolders[this.state.selectedCameraIndex]?.sessions[this.state.selectedSessionIndex]?.videos?.length }, (_, i) => i).map((number) => (
                    <MenuItem key={number} value={number}>{number + 1}</MenuItem>
                  ))}
                </TextField>

                <Button onClick={this.goToClipNumber} style={{ width: '75px', marginLeft: '10px' }}>Go</Button>
              </div>
            </div>
          )
        ) : (
          <Typography variant='body1'>Please select a recording session to view videos.</Typography>
        )}
      </div>
    );
  }
}

VideoGallery.propTypes = {
  trip: PropTypes.object.isRequired,
  videoObject: PropTypes.object,

};

export default VideoGallery;
